import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, Facebook, Instagram, ShieldCheck } from "lucide-react";
import { SITE, COUNTIES, TOWNS } from "@/data/site";
import { SERVICES } from "@/data/services";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-brand-dark text-white">
      <div className="container-x py-16 md:py-20">
        <div className="grid gap-12 lg:gap-8 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <img src={SITE.logoDark} alt="Ardeca Construction" className="h-11 w-auto" />
            <p className="mt-5 text-sm leading-relaxed text-white/70 max-w-xs">
              A family-owned construction company with {SITE.yearsExperience} years of trusted craftsmanship,
              proudly serving homeowners and businesses across Central & Entire New Jersey.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-xs font-semibold text-brand-cream">
              <ShieldCheck size={15} className="text-brand-orange" />
              Fully Licensed & Insured in NJ
            </div>
            <div className="mt-6 flex items-center gap-3">
              <a href={SITE.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="grid place-items-center h-10 w-10 rounded-full bg-white/8 hover:bg-brand-orange transition-colors">
                <Facebook size={17} />
              </a>
              <a href={SITE.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="grid place-items-center h-10 w-10 rounded-full bg-white/8 hover:bg-brand-orange transition-colors">
                <Instagram size={17} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-cream">Services</h4>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-sm text-white/70 hover:text-brand-orange transition-colors">
                    {s.grid}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-5">
            <h4 className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-cream">Get In Touch</h4>
            <ul className="mt-5 space-y-4">
              <li>
                <a href={SITE.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 text-sm text-white/80 hover:text-brand-orange transition-colors">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/8 text-brand-orange"><Phone size={16} /></span>
                  {SITE.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={SITE.emailHref} data-testid="footer-email" className="flex items-center gap-3 text-sm text-white/80 hover:text-brand-orange transition-colors break-all">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/8 text-brand-orange shrink-0"><Mail size={16} /></span>
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-white/80">
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/8 text-brand-orange"><Clock size={16} /></span>
                {SITE.hoursShort}
              </li>
              <li className="flex items-center gap-3 text-sm text-white/80">
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/8 text-brand-orange"><MapPin size={16} /></span>
                Based in {SITE.baseCity}
              </li>
            </ul>
          </div>
        </div>

        {/* Areas served */}
        <div className="mt-14 pt-10 border-t border-white/10">
          <h4 className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-cream">Proudly Serving Central & Entire New Jersey</h4>
          <div className="mt-5 flex flex-wrap gap-2">
            {COUNTIES.map((c) => (
              <span key={c} className="rounded-full bg-white/5 border border-white/10 px-3.5 py-1.5 text-xs font-medium text-white/75">{c}</span>
            ))}
          </div>
          <p className="mt-5 text-xs leading-relaxed text-white/45">
            Including {TOWNS.join(", ")} and surrounding New Jersey communities.
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col gap-4 sm:grid sm:grid-cols-3 sm:items-center">
          <p className="text-xs text-white/50 text-center sm:text-left">© {year} {SITE.name}. All rights reserved.</p>
          <p className="text-xs text-white/50 text-center order-last sm:order-none">
            Website by{" "}
            <a
              href="https://pharosgrowth.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit-pharos"
              className="font-semibold text-white/70 hover:text-brand-orange transition-colors"
            >
              Pharos Growth
            </a>
          </p>
          <div className="flex items-center justify-center sm:justify-end gap-5 text-xs text-white/50">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-brand-orange transition-colors">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-brand-orange transition-colors">Free Quote</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
